import React, { memo, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { usePopupFormContext } from "../../../../hooks/usePopupFormContext";
import { findVariantByFormValue } from "../../../../layouts/product-recommendation/utils/product-variant-utils.js";
import {
  DEFAULT_BLOCK_CSS,
  POPUP_SECTIONS,
} from "../../../../constant/constant";
import { useApp } from "../../../../context/AppState";
import {
  calculateDiscountedPrice,
  parseCssStringToObject,
  buildFontFamilyCssVar,
} from "../../../../helper";
import { formatStorefrontMoney } from "../../../../utils/format-storefront-money";
import {
  getPriceDisplay,
  PRICE_LAYOUT,
  SAVE_PILL_MODE,
} from "../../../../../shared/product-block-display.js";
import {
  collectSpecificOfferDiscountSources,
  hasActiveProductDiscount,
  normalizeProductId,
  pickOfferProductDiscountFields,
  resolveOfferProductDiscount,
} from "../../../../../shared/product-offer-discount.js";
import {
  computeOfferPriceAmounts,
  normalizeDiscountType,
  resolveVariantCompareAt,
  resolveVariantPrice,
} from "../../../../../shared/offer-price-amounts.js";
import {
  DUMMY_PREVIEW_COMPARE_AT,
  DUMMY_PREVIEW_PRICE,
  DUMMY_PREVIEW_SAVE_BADGE,
  isPreviewPlaceholderProduct,
} from "../../../../utils/dummyProductGenerator";
import styles from "./PriceBlock.module.css";

function findDiscountConfigProduct(sources, productData, matchedRuleId) {
  const productId = normalizeProductId(productData?.id);
  if (!productId) return null;

  const findInSource = (source) =>
    (source?.products || []).find(
      (entry) => normalizeProductId(entry?.id) === productId
    ) || null;

  if (matchedRuleId && String(matchedRuleId) !== "default") {
    const ruleSource = sources.find(
      (source) => String(source.offerRuleId) === String(matchedRuleId)
    );
    const fromRule = findInSource(ruleSource);
    if (fromRule) return fromRule;
  }

  const defaultSource = sources.find(
    (source) => source.offerRuleId === "default"
  );
  const fromDefault = findInSource(defaultSource);
  if (fromDefault && hasActiveProductDiscount(fromDefault)) return fromDefault;

  for (const source of sources) {
    const hit = findInSource(source);
    if (hit && hasActiveProductDiscount(hit)) return hit;
  }

  return fromDefault || findInSource(sources[0]) || null;
}

function resolveProductDiscount(popupData, productData) {
  if (productData && hasActiveProductDiscount(productData)) {
    return pickOfferProductDiscountFields(productData);
  }

  if (!popupData?.schema?.length) return {};
  const productSection = popupData.schema[0]?.section?.find(
    (s) => s?.type === "PRODUCT"
  );
  if (!productSection) return {};

  const productCond = popupData?.generalSettings?.condition?.product || {};
  const sources = collectSpecificOfferDiscountSources(productCond, {
    productSection,
  });
  const configured = findDiscountConfigProduct(
    sources,
    productData,
    popupData?.__weupsellMatchedOfferRuleId
  );

  const configList = configured
    ? [configured]
    : sources.flatMap((source) => source.products || []);

  return resolveOfferProductDiscount(
    productSection,
    productData,
    configList.length ? configList : productSection.products
  );
}

function buildSaveLabel({
  display,
  savedAmount,
  discountPercent,
  isPreviewMode = false,
  formatMoney,
}) {
  const savedNum = Number(savedAmount);
  const pct = Number(discountPercent);
  const hasSaved = Number.isFinite(savedNum) && savedNum > 0;
  const hasPct = Number.isFinite(pct) && pct > 0;
  if (!hasSaved && !hasPct) return "";

  const formattedSaved =
    hasSaved && typeof formatMoney === "function"
      ? formatMoney(savedNum)
      : "";
  const omitAmountInPreview = isPreviewMode && !formattedSaved;

  const mode = display.savePillMode || SAVE_PILL_MODE.PERCENT_AND_AMOUNT;
  if (mode === SAVE_PILL_MODE.PERCENT) {
    return hasPct ? `${pct}% OFF` : "";
  }
  if (mode === SAVE_PILL_MODE.AMOUNT) {
    if (omitAmountInPreview) return "";
    return hasSaved && formattedSaved ? `Save ${formattedSaved}` : "";
  }
  if (hasPct) return `${pct}% OFF`;
  if (omitAmountInPreview) return "";
  return hasSaved && formattedSaved ? `Save ${formattedSaved}` : "";
}

function extractPriceFontSizeFromOverride(overrideCss = "") {
  if (!overrideCss || typeof overrideCss !== "string") return null;
  const match = overrideCss.match(/\.price\s*\{[^}]*font-size:\s*([^;}\n]+)/i);
  return match?.[1]?.trim() || null;
}

function resolvePriceSectionFontSize(section, isMobile) {
  const viewport = isMobile ? "mobile" : "desktop";
  const fromCustom = section?.setting?.customCSS?.[viewport]?.fontSize;
  if (fromCustom) return fromCustom;
  return extractPriceFontSizeFromOverride(
    section?.setting?.overrideCss?.[viewport]
  );
}

function buildPriceStyleVars(blockCSS = {}, display = {}) {
  const borderWidth =
    blockCSS.borderWidth != null && blockCSS.borderWidth !== ""
      ? `${blockCSS.borderWidth}px`
      : undefined;
  const borderStyle = blockCSS.borderStyle || "solid";
  const borderColor = blockCSS.borderColor;
  const borderRadius = blockCSS.borderRadius;

  return {
    marginTop: "4px",
    ...buildFontFamilyCssVar("--price-font-family", blockCSS.fontFamily),
    ...(blockCSS.fontSize ? { "--price-font-size": blockCSS.fontSize } : {}),
    ...(blockCSS.fontWeight ? { "--price-font-weight": blockCSS.fontWeight } : {}),
    ...(blockCSS.color ? { "--price-color": blockCSS.color } : {}),
    ...(display.compareAtFontSize
      ? { "--price-compare-size": display.compareAtFontSize }
      : {}),
    ...(display.compareAtColor
      ? { "--price-compare-color": display.compareAtColor }
      : {}),
    ...(borderRadius != null && borderRadius !== ""
      ? { "--price-border-radius": borderRadius }
      : {}),
    ...(borderWidth && borderColor
      ? { "--price-border": `${borderWidth} ${borderStyle} ${borderColor}` }
      : {}),
    ...(blockCSS.backgroundColor
      ? { "--price-background": blockCSS.backgroundColor }
      : {}),
    ...(display.savePillBackground
      ? { "--price-save-bg": display.savePillBackground }
      : {}),
    ...(display.savePillColor
      ? { "--price-save-color": display.savePillColor }
      : {}),
    ...(display.savePillFontSize
      ? { "--price-save-size": display.savePillFontSize }
      : {}),
    ...(display.savePillBorderRadius != null &&
    display.savePillBorderRadius !== ""
      ? { "--price-save-radius": display.savePillBorderRadius }
      : {}),
    ...(display.savePillBorderWidth != null &&
    display.savePillBorderWidth !== ""
      ? {
          "--price-save-border": `${display.savePillBorderWidth} solid ${
            display.savePillBorderColor || "transparent"
          }`,
        }
      : {}),
  };
}

function PremiumPriceCard({
  display,
  formattedCurrentPrice,
  formattedCompareAtPrice,
  saveLabel,
  compareAmount,
  currentAmount,
}) {
  const showCompare =
    display.showCompareAt !== false &&
    compareAmount > 0 &&
    compareAmount > currentAmount;

  return (
    <div className={styles.card}>
      <div className={styles.left}>
        <div className={styles.priceRow}>
          <span className={styles.currentPrice}>{formattedCurrentPrice}</span>
          {showCompare ? (
            <span className={styles.comparePrice}>{formattedCompareAtPrice}</span>
          ) : null}
        </div>
        {display.showSubLabel !== false && display.subLabel ? (
          <span className={styles.subLabel}>{display.subLabel}</span>
        ) : null}
      </div>
      {display.showSavePill !== false && saveLabel ? (
        <span className={styles.savePill}>{saveLabel}</span>
      ) : null}
    </div>
  );
}

function InlinePriceRow({
  display,
  formattedCurrentPrice,
  formattedCompareAtPrice,
  saveLabel,
  compareAmount,
  currentAmount,
}) {
  const showCompare =
    display.showCompareAt !== false &&
    compareAmount > 0 &&
    compareAmount > currentAmount;

  const showBadge = display.showSavePill !== false && !!saveLabel;

  return (
    <div
      className={`${styles.inlineRow}${showBadge ? ` ${styles.inlineRowWithBadge}` : ""}`}
    >
      <div className={styles.inlinePrices}>
        <span className={styles.inlineCurrent}>{formattedCurrentPrice}</span>
        {showCompare ? (
          <span className={styles.inlineCompare}>{formattedCompareAtPrice}</span>
        ) : null}
      </div>
      {showBadge ? (
        <span className={styles.inlineSavePill}>{saveLabel}</span>
      ) : null}
    </div>
  );
}

function StackedPrice({
  display,
  formattedCurrentPrice,
  formattedCompareAtPrice,
  saveLabel,
  compareAmount,
  currentAmount,
}) {
  const showCompare =
    display.showCompareAt !== false &&
    compareAmount > 0 &&
    compareAmount > currentAmount;

  return (
    <div className={styles.stacked}>
      <div className={styles.stackedPrices}>
        {showCompare ? (
          <span className={styles.stackedCompare}>{formattedCompareAtPrice}</span>
        ) : null}
        <span className={styles.stackedCurrent}>{formattedCurrentPrice}</span>
      </div>
      {display.showSavePill !== false && saveLabel ? (
        <span className={styles.stackedSavePill}>{saveLabel}</span>
      ) : null}
    </div>
  );
}

const PriceBlock = ({
  productData,
  section,
  productIndex,
  popupData,
  catalogCompactPrice = false,
}) => {
  const { isMobile, appState } = useApp();
  const { watch } = usePopupFormContext();
  const display = getPriceDisplay(section);

  const selectedVariantId = watch(
    `${POPUP_SECTIONS.VARIANT_PICKER}-${productIndex}`
  );
  const selectedSellingPlanId = watch(
    `${POPUP_SECTIONS.SUBSCRIPTION_PICKER}-${productIndex}`
  );

  function getSelectedSellingPlan(sellingPlans, planId) {
    return sellingPlans?.find(
      (plan) =>
        plan.id === planId ||
        plan.id === `gid://shopify/SellingPlan/${planId}`
    );
  }

  function getSelectedVariant(data, variantId) {
    if (!data?.variants?.length) return null;
    return findVariantByFormValue(data.variants, variantId) || data.variants[0];
  }

  function calculateSellingPlanDiscount(basePrice, planPrice) {
    if (!planPrice) return 0;
    if (planPrice.adjustmentType === "PERCENTAGE") {
      return (planPrice.adjustmentValue?.percentage * basePrice) / 100;
    }
    return parseFloat(planPrice.adjustmentValue?.amount || 0);
  }

  const sellingPlans =
    productData?.sellingPlanGroups?.flatMap(
      (group) => group?.sellingPlans || group?.selling_plans || []
    ) || [];
  const selectedSellingPlan = getSelectedSellingPlan(
    sellingPlans,
    selectedSellingPlanId
  );
  const selectedSellingPlanPrice = selectedSellingPlan?.pricingPolicies?.[0];
  const selectedVariant = getSelectedVariant(productData, selectedVariantId);
  const price = resolveVariantPrice(selectedVariant);
  const compareAtPrice = resolveVariantCompareAt(selectedVariant);
  const discountAmount = calculateSellingPlanDiscount(
    price,
    selectedSellingPlanPrice
  );

  const offerDiscount = resolveProductDiscount(popupData, productData);
  const discountType = normalizeDiscountType(
    offerDiscount.discount_type || productData?.discount_type
  );
  const discountValue =
    offerDiscount.discount_value ?? productData?.discount_value;

  const { effectiveSale, compareBase, savedAmount, discountPercent } =
    computeOfferPriceAmounts({
      price,
      compareAtPrice,
      sellingPlanDiscount: discountAmount,
      discountValue,
      discountType,
      calculateDiscountedPriceFn: calculateDiscountedPrice,
    });

  const formatMoney = useCallback(
    (amount) => formatStorefrontMoney(amount, appState),
    [appState]
  );

  const isDummyPreview =
    productData?.isDummy === true || isPreviewPlaceholderProduct(productData);

  const formattedCurrentPrice = isDummyPreview
    ? DUMMY_PREVIEW_PRICE
    : effectiveSale <= 0 && Number(discountValue) > 0
      ? "Free"
      : formatMoney(effectiveSale);
  const formattedCompareAtPrice = isDummyPreview
    ? display.showCompareAt === false
      ? ""
      : DUMMY_PREVIEW_COMPARE_AT
    : compareBase
      ? formatMoney(compareBase)
      : "";

  const saveLabel = useMemo(() => {
    if (isDummyPreview) {
      return display.showSavePill === false ? "" : DUMMY_PREVIEW_SAVE_BADGE;
    }
    return buildSaveLabel({
      display,
      savedAmount,
      discountPercent,
      isPreviewMode: appState?.is_preview_mode,
      formatMoney,
    });
  }, [
    isDummyPreview,
    display,
    savedAmount,
    discountPercent,
    appState?.is_preview_mode,
    formatMoney,
  ]);

  const resolvedPriceFontSize = resolvePriceSectionFontSize(section, isMobile);

  const blockCSS = {
    ...DEFAULT_BLOCK_CSS,
    ...(catalogCompactPrice
      ? {
          height: "auto",
          width: "100%",
          margin: 0,
          marginTop: 0,
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
        }
      : {}),
    ...section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"],
    ...parseCssStringToObject(
      section?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]
    ),
    ...(resolvedPriceFontSize ? { fontSize: resolvedPriceFontSize } : {}),
  };

  if (catalogCompactPrice) {
    blockCSS.margin = 0;
    blockCSS.marginTop = 0;
    blockCSS.height = "auto";
    blockCSS.maxHeight = "none";
  }

  const layout = display.layout || PRICE_LAYOUT.PREMIUM_CARD_V2;
  const priceProps = {
    display,
    formattedCurrentPrice,
    formattedCompareAtPrice,
    // Dummy placeholders are non-numeric ("XXX.XX"); force compare/badge visibility checks.
    currentAmount: isDummyPreview ? 1 : effectiveSale,
    compareAmount: isDummyPreview
      ? display.showCompareAt === false
        ? 0
        : 2
      : compareBase,
    saveLabel,
  };

  const priceStyleVars = buildPriceStyleVars(blockCSS, display);

  return (
    <>
      <style>
        {section?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]}
      </style>
      <div data-weupsell-price-block style={{ ...blockCSS, ...priceStyleVars }}>
        {layout === PRICE_LAYOUT.INLINE_ROW ? (
          <InlinePriceRow {...priceProps} />
        ) : layout === PRICE_LAYOUT.STACKED_COMPACT ? (
          <StackedPrice {...priceProps} />
        ) : (
          <PremiumPriceCard {...priceProps} />
        )}
      </div>
    </>
  );
};

PriceBlock.propTypes = {
  productData: PropTypes.object,
  section: PropTypes.object,
  popupData: PropTypes.object,
  productIndex: PropTypes.number,
  catalogCompactPrice: PropTypes.bool,
};

PriceBlock.defaultProps = {
  section: {},
};

export default memo(PriceBlock);
